import { JackpotType } from 'game-ui-types'

// Bottle is the bridge to the engine runtime. These settings contain the game asset paths and atlas names.
export const getBottleGameSettings = () => window.bottle?.container?.GameSettings

// Engine type can arrive as MobileBaseSlot/DesktopBaseSlot, but the assets folder is BaseSlot.
export const normalizeEngineName = (engineName: string) => engineName.replace(/^(Mobile|Desktop)/i, '')

// URL resolution is sensitive to missing trailing slashes because paths like "images/" are resolved as siblings otherwise.
const ensureTrailingSlash = (path: string) => (path.endsWith('/') ? path : `${path}/`)

// Promo mode means the engine loaded a promo game type different from the original/base game type.
const isPromoMode = () => {
    const gameSettings = getBottleGameSettings()

    return Boolean(gameSettings?.promoGameType && gameSettings.gameType !== gameSettings.promoGameType)
}

// Checks whether Bottle has the minimum data needed to construct asset paths without crashing.
export const hasBottleGameSettings = () => {
    const gameSettings = getBottleGameSettings()

    return Boolean(
        gameSettings?.engineType &&
        gameSettings.gameIdentificationNumber !== undefined &&
        gameSettings.gameIdentificationNumber !== null
    )
}

// Returns the game root path, for example:
// https://cdn/Mobile/games/BaseSlot/assets/801/
// Promo paths need this root because promo assets live beside images/, under promoModes/{mode}/images/.
const getGameTypeBasePath = () => {
    const gameSettings = getBottleGameSettings()
    const engineName = gameSettings?.engineType ? normalizeEngineName(gameSettings.engineType) : undefined
    const gameAssetsPath = gameSettings?.assetsPath ?? gameSettings?.gameIdentificationNumber
    const configuredAssetsBasePath = gameSettings?.gameAssetsBasePath ?? gameSettings?.assetsBasePath

    if (gameSettings?.gameTypePath) {
        return new URL(ensureTrailingSlash(gameSettings.gameTypePath), window.location.href).toString()
    }

    if (configuredAssetsBasePath) {
        // Legacy/test override fields point to the images folder, so go one level up to recover the game root.
        const configuredImagesPath = new URL(ensureTrailingSlash(configuredAssetsBasePath), window.location.href)

        return new URL('../', configuredImagesPath).toString()
    }

    if (!engineName || gameAssetsPath === undefined || gameAssetsPath === null) {
        return window.location.href
    }

    return new URL(`/Mobile/games/${engineName}/assets/${gameAssetsPath}/`, window.location.origin).toString()
}

// Returns the normal images folder path, for example:
// https://cdn/Mobile/games/BaseSlot/assets/801/images/
export const getGameAssetsBasePath = () => {
    return new URL('images/', getGameTypeBasePath()).toString()
}

// Common assets live outside the per-game asset folder, under /Mobile/games/assets/commonAssets/.
const getCommonAssetsBasePath = () => {
    return new URL('/Mobile/games/assets/commonAssets/', window.location.origin).toString()
}

// Builds an absolute promo atlas path from the game root, avoiding emulator/proxy relative URL issues.
const getPromoAtlasPath = (atlasPath: string) => {
    const promoImagesPath = new URL(
        `promoModes/${getBottleGameSettings()?.promoGameType}/images/`,
        getGameTypeBasePath()
    )

    return new URL(atlasPath, promoImagesPath).toString()
}

// Chooses between normal images and promo images depending on the current game mode.
const getGameAtlasPath = (atlasPath: string, shouldUsePromoMode = false) =>
    shouldUsePromoMode && isPromoMode() ? getPromoAtlasPath(atlasPath) : atlasPath

// Adds the engine cache timestamp as v=... while preserving any existing query parameters.
export const withCacheTimestamp = (atlasPath: string, cacheTimestamp = getBottleGameSettings()?.cacheTimestamp) => {
    if (!cacheTimestamp) {
        return atlasPath
    }

    const queryStartIndex = atlasPath.indexOf('?')
    const path = queryStartIndex === -1 ? atlasPath : atlasPath.slice(0, queryStartIndex)
    const query = queryStartIndex === -1 ? '' : atlasPath.slice(queryStartIndex + 1)
    const searchParams = new URLSearchParams(query)

    searchParams.set('v', String(cacheTimestamp))

    return `${path}?${searchParams.toString()}`
}

// Game settings can provide one atlas name or many atlas names; normalize both cases to an array.
const toArray = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.flatMap(toArray)
    }

    if (typeof value === 'string') {
        return value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
    }

    if (value && typeof value === 'object' && 'length' in value && typeof value.length === 'number') {
        return Array.from({ length: value.length }, (_, index) => (value as Record<number, unknown>)[index]).flatMap(
            toArray
        )
    }

    if (value && typeof value === 'object') {
        return Object.values(value).flatMap(toArray)
    }

    return []
}

// InfoLine uses small reel atlases when present, otherwise normal reel atlases.
// In promo mode it follows the promo path unless the engine explicitly says to use original reels.
export const getInfoLineAtlasPaths = (jackpotType?: JackpotType) => {
    const gameSettings = getBottleGameSettings()
    const smallReelImages = toArray(gameSettings?.smallReelImages)
    const reelImages = toArray(gameSettings?.reelImages)
    const atlasNames = smallReelImages.length > 0 ? smallReelImages : reelImages
    const fallbackAtlasNames = ['smallReelImages', 'reelImages']
    const shouldUsePromoMode = !gameSettings?.loadOriginalReelsInPromoMode
    const isJackpotLink = jackpotType === JackpotType.JACKPOT_LINK || gameSettings?.configVariant === 'JackpotLink'
    const commonAssetsCacheTimestamp = gameSettings?.commonAssetsCacheTimestamp ?? gameSettings?.cacheTimestamp

    const atlasPaths = (atlasNames.length > 0 ? atlasNames : fallbackAtlasNames)
        .map((atlasPath) => getGameAtlasPath(atlasPath, shouldUsePromoMode))
        .map(withCacheTimestamp)

    if (isJackpotLink) {
        atlasPaths.push(
            withCacheTimestamp(
                new URL('mobileImages/jackpotLinkAssets/reels/reelImages.json', getCommonAssetsBasePath()).toString(),
                commonAssetsCacheTimestamp
            )
        )
    }

    return [...new Set(atlasPaths)]
}

// Buy Bonus external UI atlas always follows promo assets when the game is in promo mode.
export const getBuyBonusAtlasPaths = () => [withCacheTimestamp(getGameAtlasPath('buyBonus/buyBonusExternalUI', true))]
